import React, { useState } from 'react'

const MODES = [
  { value: 'july',   label: 'July' },
  { value: 'august', label: 'August' },
  { value: 'both',   label: 'July and August' },
]

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function formatMonthDay(iso) {
  if (!iso) return ''
  const parts = iso.split('-')
  const month = parseInt(parts[1])
  const day   = parseInt(parts[2])
  return `${MONTH_NAMES[month - 1]} ${day}`
}

function inferMode(startDate, endDate) {
  if (!startDate || !endDate) return null
  const s = startDate.slice(5) // MM-DD
  const e = endDate.slice(5)
  if (s === '07-01' && e === '07-31') return 'july'
  if (s === '08-01' && e === '08-31') return 'august'
  if (s === '07-01' && e === '08-31') return 'both'
  return 'custom'
}

function parseIso(iso) {
  if (!iso) return { month: 7, day: 1 }
  const parts = iso.split('-')
  return { month: parseInt(parts[1]) || 7, day: parseInt(parts[2]) || 1 }
}

function toIso(month, day) {
  return `2000-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function daysInMonth(month) {
  return new Date(2000, month, 0).getDate()
}

function MonthDayPicker({ label, value, onChange }) {
  const { month, day } = parseIso(value)
  const months = Array.from({ length: 12 }, (_, i) => i + 1)
  const days = Array.from({ length: daysInMonth(month) }, (_, i) => i + 1)

  function handleMonthChange(e) {
    const m = parseInt(e.target.value)
    const clampedDay = Math.min(day, daysInMonth(m))
    onChange(toIso(m, clampedDay))
  }

  function handleDayChange(e) {
    onChange(toIso(month, parseInt(e.target.value)))
  }

  return (
    <div className='dome-d-flex dome-flex-column dome-gap6'>
      <span className='dome-p2 dome-text-w500'>{label}</span>
      <div className='dome-d-flex dome-gap6'>
        <select value={month} onChange={handleMonthChange} className='dome-input'>
          {months.map(m => (
            <option key={m} value={m}>{MONTH_NAMES[m - 1]}</option>
          ))}
        </select>
        <select value={day} onChange={handleDayChange} className='dome-input'>
          {days.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
    </div>
  )
}

function ModeButton({ value, label, mode, enabled, onChange }) {
  return (
    <React.Fragment>
      <input
        type='radio'
        name='summer_recess[mode]'
        form='update-building-form'
        value={value}
        checked={mode === value}
        onChange={() => onChange(value)}
        style={{ display: 'none' }}
      />
      <button
        type='button'
        className={`dome-btn dome-btn-base ${mode === value ? 'dome-btn-dark-green' : 'dome-btn-grey-stroke'}`}
        style={{ flex: '1 1 0', minWidth: 0, height: 50 }}
        disabled={!enabled}
        onClick={() => onChange(value)}
      >
        {label}
      </button>
    </React.Fragment>
  )
}

export default function BoardSummerRecess({
  enabled: initialEnabled,
  startDate: initialStart,
  endDate: initialEnd,
  isSuperAdmin
}) {
  const detectedMode = inferMode(initialStart, initialEnd)
  const [enabled, setEnabled]         = useState(initialEnabled)
  const [mode, setMode]               = useState(detectedMode !== 'custom' ? detectedMode : null)
  const [customStart, setCustomStart] = useState(initialStart || toIso(7, 1))
  const [customEnd, setCustomEnd]     = useState(initialEnd   || toIso(8, 31))
  const [showChange, setShowChange]   = useState(false)
  const isCustom = mode === 'custom'
  const isNonAdminCustom = !isSuperAdmin && detectedMode === 'custom'

  return (
    <div className='board-summer-recess dome-d-flex dome-flex-column dome-gap12'>
      <input form='update-building-form' type='hidden' name='summer_recess[enabled]' value={enabled ? '1' : '0'} />

      <div className='dome-d-flex dome-flex-column dome-gap6'>
        <span className='dome-p3 dome-text-w500'>Does this board observe summer recess?</span>
        <div className='board-summer-recess__enabled dome-d-flex dome-gap12' style={{ width: '100%' }}>
          <button
            type='button'
            className={`dome-btn dome-btn-base ${enabled ? 'dome-btn-dark-green' : 'dome-btn-grey-stroke'}`}
            style={{ flex: '1 1 0', minWidth: 0, height: 50 }}
            onClick={() => setEnabled(true)}
          >
            Enabled
          </button>
          <button
            type='button'
            className={`dome-btn dome-btn-base ${!enabled ? 'dome-btn-dark-green' : 'dome-btn-grey-stroke'}`}
            style={{ flex: '1 1 0', minWidth: 0, height: 50 }}
            onClick={() => { setEnabled(false); setMode(null) }}
          >
            Not Enabled
          </button>
        </div>
      </div>

      {enabled && (
        <>
          <div className='dome-d-flex dome-flex-column dome-gap6'>
            <span className='dome-p3 dome-text-w500'>Which month(s) does the board take recess?</span>
            {isNonAdminCustom && !showChange ? (
              <div className='dome-d-flex dome-flex-column dome-gap6'>
                <span className='dome-p3 dome-color-darkest-grey'>
                  Summer Recess is set to {formatMonthDay(initialStart)} – {formatMonthDay(initialEnd)}
                </span>
                <button
                  type='button'
                  className='dome-btn dome-btn-link dome-p3'
                  style={{ alignSelf: 'flex-start', padding: 0 }}
                  onClick={() => setShowChange(true)}
                >
                  Change
                </button>
              </div>
            ) : (
              <div className='board-summer-recess__modes dome-d-flex dome-gap12' style={{ width: '100%' }}>
                {MODES.map(({ value, label }) => (
                  <ModeButton key={value} value={value} label={label} mode={mode} enabled={enabled} onChange={setMode} />
                ))}
                {isSuperAdmin && (
                  <ModeButton value='custom' label='Custom' mode={mode} enabled={enabled} onChange={setMode} />
                )}
              </div>
            )}
          </div>

          {isCustom && isSuperAdmin && (
            <div className='board-summer-recess__custom-dates dome-d-flex dome-gap18'>
              <MonthDayPicker
                label='Start'
                value={customStart}
                onChange={setCustomStart}
              />
              <input type='hidden' name='summer_recess[start]' form='update-building-form' value={customStart} />
              <MonthDayPicker
                label='End'
                value={customEnd}
                onChange={setCustomEnd}
              />
              <input type='hidden' name='summer_recess[end]' form='update-building-form' value={customEnd} />
            </div>
          )}
        </>
      )}
    </div>
  )
}
